import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AiAssistService } from 'src/app/services/ai-assist.service';
import { environment } from 'src/environments/environment';
import { NotifierService } from 'angular-notifier';

interface WordTiming {
  word: string;
  startTime: number;
  endTime: number;
  speaker?: string;
}

interface SpeakerBlock {
  speaker: string;
  words: WordTiming[];
}

@Component({
  selector: 'app-bouncing-ball',
  templateUrl: './bouncing-ball.component.html',
  styleUrls: ['./bouncing-ball.component.css']
})
export class BouncingBallComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;

  isPlaying = false;
  currentTime = 0;
  duration = 0;
  isLoading = true;
  jobId: string = '';
  presignedUrl: string = '';
  jobData: any = {}; // Store the job data
  
  private animationFrameId: any;

  // Word timings from API
  wordTimings: WordTiming[] = [];
  speakerBlocks: SpeakerBlock[] = [];
  activeWordIndex: number = -1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private aiAssist: AiAssistService,
    private notifier: NotifierService
  ) {
  }

  ngOnInit(): void {
    // Get query parameters
    this.route.queryParams.subscribe(params => {
      this.jobData = {
        referenceId: params['referenceId'] || '',
        fileId: params['fileId'] || '',
        status: params['status'] || ''
      };
      console.log('Received job data:', this.jobData);
    });

    this.route.params.subscribe(params => {
      this.jobId = params['jobId'];
      if (this.jobId) {
        this.loadSpeechData();
      }
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  async loadSpeechData(): Promise<void> {
    try {
      this.isLoading = true;

      // Get presigned URL
      const presignedUrlResponse = await this.aiAssist.getPresignedURL(this.jobId).toPromise();
      this.presignedUrl = this.aiAssist.getJSONResponse(presignedUrlResponse).data;

      // Get speech data
      const formData = new FormData();
      formData.append('jobId', this.jobId);
      
      const speechResponse = await this.aiAssist.getSpeechResult(
        environment.url + '/api/v2/job/result', 
        formData
      ).toPromise();
      
      const speechData = this.aiAssist.getJSONResponse(speechResponse).data.transcriptionData;
      const job_data = this.aiAssist.getJSONResponse(await this.aiAssist.getJobDetails(this.jobId).toPromise());
      console.log('Job Data:', job_data);
      // Process word timings
      this.processWordTimings(speechData);

      // Set audio source
      setTimeout(() => {
        if (this.audioPlayer) {
          this.audioPlayer.nativeElement.src = this.presignedUrl;
        }
      }, 100);

      this.isLoading = false;
    } catch (error) {
      console.error('Error loading speech data:', error);
      this.isLoading = false;
      this.notifier.notify('error', 'Failed to load speech data');
    }
  }

  processWordTimings(speechData: any): void {
    this.wordTimings = [];
    this.speakerBlocks = [];

    if (speechData && speechData.links && Array.isArray(speechData.links)) {
      const links = speechData.links;
      let currentSpeaker: string | null = null;
      let currentBlock: WordTiming[] = [];

      for (const link of links) {
        const word = link.word?.trim();
        if (!word) continue;

        const speaker = link.speaker || link.spk || 'SPK';
        const startTime = this.asSeconds(link.start || 0);
        const endTime = this.asSeconds(link.end || startTime + 0.001);

        const wordTiming: WordTiming = {
          word: word,
          startTime: startTime,
          endTime: endTime,
          speaker: speaker
        };

        this.wordTimings.push(wordTiming);

        // Group by speaker
        if (currentSpeaker === null) {
          currentSpeaker = speaker;
        } else if (speaker !== currentSpeaker) {
          if (currentBlock.length > 0) {
            this.speakerBlocks.push({
              speaker: currentSpeaker,
              words: [...currentBlock]
            });
            currentBlock = [];
          }
          currentSpeaker = speaker;
        }

        currentBlock.push(wordTiming);
      }

      // Flush final block
      if (currentBlock.length > 0 && currentSpeaker) {
        this.speakerBlocks.push({
          speaker: currentSpeaker,
          words: currentBlock
        });
      }
    }

    console.log('Word timings:', this.wordTimings.length);
    console.log('Speaker blocks:', this.speakerBlocks.length);
  }

  asSeconds(value: any): number {
    if (typeof value === 'number') {
      return value / 1000;
    }
    if (typeof value === 'string') {
      return parseFloat(value) / 1000 || 0;
    }
    return 0;
  }

  onPlay(): void {
    this.isPlaying = true;
    this.startTracking();
  }

  onPause(): void {
    this.isPlaying = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  onTimeUpdate(): void {
    const player = this.audioPlayer.nativeElement;
    this.currentTime = player.currentTime;
    this.duration = player.duration || 0;
  }

  onLoadedMetadata(): void {
    const player = this.audioPlayer.nativeElement;
    this.duration = player.duration;
  }

  startTracking(): void {
    const track = () => {
      if (this.isPlaying) {
        this.updateActiveWord();
        this.animationFrameId = requestAnimationFrame(track);
      }
    };
    track();
  }

  updateActiveWord(): void {
    const player = this.audioPlayer.nativeElement;
    this.currentTime = player.currentTime;

    // Find active word
    let foundIndex = -1;
    for (let i = 0; i < this.wordTimings.length; i++) {
      const timing = this.wordTimings[i];
      if (this.currentTime >= timing.startTime && this.currentTime < timing.endTime) {
        foundIndex = i;
        break;
      }
    }

    // Update active index and scroll
    if (foundIndex !== this.activeWordIndex) {
      this.activeWordIndex = foundIndex;
      
      if (foundIndex >= 0) {
        this.scrollToWord(foundIndex);
      }
    }
  }

  scrollToWord(index: number): void {
    const element = document.getElementById('word-' + index);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  getGlobalWordIndex(block: SpeakerBlock, localIndex: number): number {
    let globalIndex = 0;
    for (const b of this.speakerBlocks) {
      if (b === block) {
        return globalIndex + localIndex;
      }
      globalIndex += b.words.length;
    }
    return -1;
  }

  isWordPassed(word: WordTiming): boolean {
    return this.currentTime >= word.endTime;
  }

  seekTo(time: number): void {
    const player = this.audioPlayer.nativeElement;
    player.currentTime = time;
    this.currentTime = time;
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }

  get progressPercentage(): number {
    return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }
}
